import React, { useState } from 'react';
import styled from 'styled-components';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  Card,
  CardTitle,
  CardContent,
  ActionBar,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn
} from '../styles/PageStyles';
import { faFileShield } from '@fortawesome/free-solid-svg-icons';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';

// Styled components for the policies page
const PolicyCard = styled(Card)`
  margin-bottom: 1.5rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background: #ffffff;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.08);
  }
`;

const PolicyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const PolicyTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const VersionTag = styled.span`
  font-size: 0.8rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  background: rgba(0, 102, 255, 0.1);
  color: #0066FF;
`;

const PolicyStatus = styled.div`
  font-size: 0.8rem;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  background: ${props =>
    props.status === 'Active' ? 'rgba(52, 199, 89, 0.1)' :
      props.status === 'Draft' ? 'rgba(142, 142, 147, 0.1)' :
        props.status === 'Deprecated' ? 'rgba(255, 59, 48, 0.1)' :
          'rgba(0, 102, 255, 0.1)'
  };
  color: ${props =>
    props.status === 'Active' ? 'var(--success)' :
      props.status === 'Draft' ? 'var(--text-secondary)' :
        props.status === 'Deprecated' ? 'var(--danger)' :
          '#0066FF'
  };
`;

const PolicyTags = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const PolicyTag = styled.div`
  font-size: 0.8rem;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  background: rgba(0, 102, 255, 0.05);
  color: #0066FF;
`;

const PolicyDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const PolicySection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.8rem;
`;

const RulesList = styled.ul`
  padding-left: 1.5rem;
  margin-bottom: 1.5rem;
`;

const RuleItem = styled.li`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
  line-height: 1.5;
`;

const PolicyFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const PolicyMeta = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const MetaItem = styled.div``;

const PolicyActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ComplianceTable = styled(Table)`
  margin-top: 1rem;
  background: #ffffff;
`;

const ComplianceStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ComplianceIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props =>
    props.status === 'Compliant' ? 'var(--success)' :
      props.status === 'Warning' ? 'var(--warning)' :
        props.status === 'Violation' ? 'var(--danger)' :
          'var(--text-secondary)'
  };
`;

const FilterBar = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  background: #ffffff;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? '#0066FF' : '#ffffff'};
  color: ${props => props.active ? '#ffffff' : 'var(--text-primary)'};
  border: 1px solid ${props => props.active ? '#0066FF' : 'var(--border-color)'};
  border-radius: 20px;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#0066FF' : 'rgba(0, 102, 255, 0.05)'};
  }
`;

const VersionHistoryModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  
  &:hover {
    color: #0066FF;
  }
`;

const VersionItem = styled.div`
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  margin-bottom: 1rem;
  background: #ffffff;
`;

const VersionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const VersionNumber = styled.span`
  font-weight: 600;
  color: #0066FF;
`;

const VersionDate = styled.span`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const VersionChanges = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
`;

const Policies = () => {
  const [activeTab, setActiveTab] = useState('policies');
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');

  // Filter policies based on search term and filters
  const filteredPolicies = securityPolicies.filter(policy => {
    // Search term filter
    const matchesSearch =
      policy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      policy.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      policy.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      policy.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    // Status filter
    const matchesStatus = statusFilter === 'all' || policy.status === statusFilter;

    // Category filter
    const matchesCategory = categoryFilter === 'all' || policy.category === categoryFilter;

    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Get all unique categories
  const categories = [...new Set(securityPolicies.map(policy => policy.category))];

  // Get all compliance issues across all policies
  const allComplianceIssues = securityPolicies.flatMap(policy =>
    policy.complianceStatus.map(status => ({
      policyId: policy.id,
      policyName: policy.name,
      ...status
    }))
  );

  // Filter compliance issues
  const filteredComplianceIssues = allComplianceIssues.filter(issue => {
    const matchesSearch =
      issue.agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.pipelineId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.policyName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCompliance = complianceFilter === 'all' || issue.status === complianceFilter;

    return matchesSearch && matchesCompliance;
  });

  const openVersionHistory = (policy) => {
    setSelectedPolicy(policy);
    setShowVersionHistory(true);
  };

  const closeVersionHistory = () => {
    setShowVersionHistory(false);
  };

  return (
    <PageContainer>
      {/* <PageHeader>
        <div>
          <PageTitle>Security Policies & Guardrails</PageTitle>
          <PageDescription>
            Manage security policies and guardrails for agents and pipelines. Monitor compliance and version history.
          </PageDescription>
        </div>
        <ActionBar>
          <Button>
            <i className="fas fa-sync-alt"></i> Refresh
          </Button>
          <Button primary>
            <i className="fas fa-plus"></i> New Policy
          </Button>
        </ActionBar>
      </PageHeader> */}


      <PageHeaderWithInfo
        icon={faFileShield}
        title="Security Policies & Guardrails"
        description="Manage security policies and guardrails for agents and pipelines. Monitor compliance and version history."
      >
        <div style={{ display: 'flex', gap: '1rem' }}>
          <SearchInput
            placeholder="Search policies by name, ID, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button>Refresh</Button>
          <Button primary>New Policy</Button>
        </div>
      </PageHeaderWithInfo>


      <TabContainer>
        <TabList>
          <Tab
            active={activeTab === 'policies'}
            onClick={() => setActiveTab('policies')}
          >
            Policies
          </Tab>
          <Tab
            active={activeTab === 'compliance'}
            onClick={() => setActiveTab('compliance')}
          >
            Compliance Audit
          </Tab>
        </TabList>

        <TabContent active={activeTab === 'policies'}>
          <div style={{ marginBottom: '1rem' }}>

          </div>

          <FilterBar>
            <span style={{ marginRight: '0.5rem', color: 'var(--text-secondary)' }}>Status:</span>
            <FilterButton
              active={statusFilter === 'all'}
              onClick={() => setStatusFilter('all')}
            >
              All
            </FilterButton>
            <FilterButton
              active={statusFilter === 'Active'}
              onClick={() => setStatusFilter('Active')}
            >
              Active
            </FilterButton>
            <FilterButton
              active={statusFilter === 'Draft'}
              onClick={() => setStatusFilter('Draft')}
            >
              Draft
            </FilterButton>
            <FilterButton
              active={statusFilter === 'Deprecated'}
              onClick={() => setStatusFilter('Deprecated')}
            >
              Deprecated
            </FilterButton>

            <span style={{ marginLeft: '1rem', marginRight: '0.5rem', color: 'var(--text-secondary)' }}>Category:</span>
            <FilterButton
              active={categoryFilter === 'all'}
              onClick={() => setCategoryFilter('all')}
            >
              All
            </FilterButton>
            {categories.map(category => (
              <FilterButton
                key={category}
                active={categoryFilter === category}
                onClick={() => setCategoryFilter(category)}
              >
                {category}
              </FilterButton>
            ))}
          </FilterBar>

          <ContentGrid>
            {filteredPolicies.map(policy => (
              <PolicyCard key={policy.id}>
                <PolicyHeader>
                  <PolicyTitle>
                    {policy.name}
                    <VersionTag>v{policy.version}</VersionTag>
                  </PolicyTitle>
                  <PolicyStatus status={policy.status}>{policy.status}</PolicyStatus>
                </PolicyHeader>

                <PolicyDescription>{policy.description}</PolicyDescription>

                <PolicyTags>
                  <PolicyTag>{policy.category}</PolicyTag>
                  {policy.tags.map(tag => (
                    <PolicyTag key={tag}>{tag}</PolicyTag>
                  ))}
                </PolicyTags>

                <PolicySection>
                  <SectionTitle>Rules</SectionTitle>
                  <RulesList>
                    {policy.rules.map((rule, index) => (
                      <RuleItem key={index}>{rule}</RuleItem>
                    ))}
                  </RulesList>
                </PolicySection>

                <PolicySection>
                  <SectionTitle>Applies To</SectionTitle>
                  <PolicyDescription>{policy.appliesTo.join(', ')}</PolicyDescription>
                </PolicySection>

                {policy.complianceStatus && policy.complianceStatus.length > 0 && (
                  <PolicySection>
                    <SectionTitle>Compliance Status</SectionTitle>
                    <ComplianceTable>
                      <TableHeader>
                        <TableCell>Agent</TableCell>
                        <TableCell>Pipeline</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Last Checked</TableCell>
                      </TableHeader>
                      {policy.complianceStatus.slice(0, 3).map((status, index) => (
                        <TableRow key={index}>
                          <TableCell>{status.agentName}</TableCell>
                          <TableCell>{status.pipelineId}</TableCell>
                          <TableCell>
                            <ComplianceStatus>
                              <ComplianceIndicator status={status.status} />
                              {status.status}
                            </ComplianceStatus>
                          </TableCell>
                          <TableCell>{status.lastChecked}</TableCell>
                        </TableRow>
                      ))}
                      {policy.complianceStatus.length > 3 && (
                        <TableRow>
                          <TableCell colSpan="4" style={{ textAlign: 'center' }}>
                            <Button onClick={() => setActiveTab('compliance')}>
                              View All ({policy.complianceStatus.length})
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </ComplianceTable>
                  </PolicySection>
                )}

                <PolicyFooter>
                  <PolicyMeta>
                    <MetaItem>Created: {policy.createdAt}</MetaItem>
                    <MetaItem>Updated: {policy.updatedAt}</MetaItem>
                  </PolicyMeta>
                  <PolicyActions>
                    <Button onClick={() => openVersionHistory(policy)}>
                      <i className="fas fa-history"></i> Version History
                    </Button>
                    <Button>
                      <i className="fas fa-edit"></i> Edit
                    </Button>
                  </PolicyActions>
                </PolicyFooter>
              </PolicyCard>
            ))}
          </ContentGrid>
        </TabContent>

        <TabContent active={activeTab === 'compliance'}>
          <div style={{ marginBottom: '1rem' }}>
            <SearchInput
              placeholder="Search compliance by agent, pipeline, or policy..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <FilterBar>
            <span style={{ marginRight: '0.5rem', color: 'var(--text-secondary)' }}>Status:</span>
            <FilterButton
              active={complianceFilter === 'all'}
              onClick={() => setComplianceFilter('all')}
            >
              All
            </FilterButton>
            <FilterButton
              active={complianceFilter === 'Compliant'}
              onClick={() => setComplianceFilter('Compliant')}
            >
              Compliant
            </FilterButton>
            <FilterButton
              active={complianceFilter === 'Warning'}
              onClick={() => setComplianceFilter('Warning')}
            >
              Warning
            </FilterButton>
            <FilterButton
              active={complianceFilter === 'Violation'}
              onClick={() => setComplianceFilter('Violation')}
            >
              Violation
            </FilterButton>
          </FilterBar>

          <Table>
            <TableHeader>
              <TableCell>Agent</TableCell>
              <TableCell>Pipeline</TableCell>
              <TableCell>Policy</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Checked</TableCell>
              <TableCell>Issues</TableCell>
              <TableCell>Actions</TableCell>
            </TableHeader>
            {filteredComplianceIssues.map((issue, index) => (
              <TableRow key={index}>
                <TableCell>{issue.agentName}</TableCell>
                <TableCell>{issue.pipelineId}</TableCell>
                <TableCell>{issue.policyName}</TableCell>
                <TableCell>
                  <ComplianceStatus>
                    <ComplianceIndicator status={issue.status} />
                    {issue.status}
                  </ComplianceStatus>
                </TableCell>
                <TableCell>{issue.lastChecked}</TableCell>
                <TableCell>
                  {issue.issues ? issue.issues.join(', ') : 'None'}
                </TableCell>
                <TableCell>
                  <Button>Remediate</Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredComplianceIssues.length === 0 && (
              <TableRow>
                <TableCell colSpan="7" style={{ textAlign: 'center' }}>
                  No compliance issues found matching your filters.
                </TableCell>
              </TableRow>
            )}
          </Table>
        </TabContent>
      </TabContainer>

      {showVersionHistory && selectedPolicy && (
        <VersionHistoryModal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Version History - {selectedPolicy.name}</ModalTitle>
              <CloseButton onClick={closeVersionHistory}>×</CloseButton>
            </ModalHeader>

            {selectedPolicy.versionHistory.map((version, index) => (
              <VersionItem key={index}>
                <VersionHeader>
                  <VersionNumber>
                    Version {version.version}
                    {version.version === selectedPolicy.version && (
                      <span style={{
                        fontSize: '0.8rem',
                        padding: '0.1rem 0.4rem',
                        borderRadius: '4px',
                        background: 'rgba(52, 199, 89, 0.1)',
                        color: '#34C759',
                        marginLeft: '0.5rem'
                      }}>
                        Current
                      </span>
                    )}
                  </VersionNumber>
                  <VersionDate>{version.date}</VersionDate>
                </VersionHeader>
                <VersionChanges>{version.changes}</VersionChanges>
              </VersionItem>
            ))}

            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={closeVersionHistory}>Close</Button>
            </div>
          </ModalContent>
        </VersionHistoryModal>
      )}
    </PageContainer>
  );
};

const securityPolicies = [
  {
    id: 'POL-SEC-001',
    name: 'Data Access Control',
    description: 'Defines the rules for agent access to sensitive data and information systems.',
    version: '2.3',
    status: 'Active',
    category: 'Data Security',
    tags: ['PII', 'Data Access', 'GDPR'],
    rules: [
      'Agents must authenticate using OAuth 2.0 before accessing any data.',
      'Access to PII data requires explicit user consent.',
      'All data access must be logged with timestamp, agent ID, and purpose.',
      'Data should be accessed on a need-to-know basis only.',
      'Sensitive data must be encrypted during transmission and at rest.'
    ],
    appliesTo: ['Document Processing Agents', 'Customer Service Agents', 'Data Analysis Pipelines'],
    createdBy: 'Alex Johnson',
    createdAt: '2024-11-15',
    updatedAt: '2025-02-20',
    versionHistory: [
      { version: '2.3', date: '2025-02-20', changes: 'Added GDPR compliance requirements.' },
      { version: '2.2', date: '2025-01-10', changes: 'Updated authentication requirements to OAuth 2.0.' },
      { version: '2.1', date: '2024-12-05', changes: 'Added logging requirements for all data access.' },
      { version: '2.0', date: '2024-11-15', changes: 'Major revision with new rules for data encryption.' },
      { version: '1.0', date: '2024-10-01', changes: 'Initial policy creation.' }
    ],
    complianceStatus: [
      { agentId: 'AGT-123', agentName: 'Claims Processor', pipelineId: 'PIP-001', status: 'Compliant', lastChecked: '2025-03-17' },
      { agentId: 'AGT-145', agentName: 'Customer Support', pipelineId: 'PIP-003', status: 'Warning', lastChecked: '2025-03-16', issues: ['Incomplete logging'] },
      { agentId: 'AGT-156', agentName: 'Data Analyzer', pipelineId: 'PIP-007', status: 'Violation', lastChecked: '2025-03-15', issues: ['Unauthorized PII access', 'Missing encryption'] }
    ]
  },
  {
    id: 'POL-SEC-002',
    name: 'Output Sanitization',
    description: 'Ensures that all agent outputs are properly sanitized to prevent harmful content.',
    version: '1.5',
    status: 'Active',
    category: 'Content Safety',
    tags: ['Content Moderation', 'Safety', 'Output Filtering'],
    rules: [
      'All agent outputs must be scanned for harmful content before delivery.',
      'Outputs containing personal identifiers must be redacted unless explicitly required.',
      'Profanity and offensive language must be filtered from all outputs.',
      'Agents must not generate content that could be used for malicious purposes.',
      'Output should be checked for hallucinations and factual accuracy.'
    ],
    appliesTo: ['Content Generation Agents', 'Customer-Facing Agents', 'All Public Pipelines'],
    createdBy: 'Maria Garcia',
    createdAt: '2024-12-10',
    updatedAt: '2025-03-01',
    versionHistory: [
      { version: '1.5', date: '2025-03-01', changes: 'Added hallucination detection requirements.' },
      { version: '1.4', date: '2025-02-15', changes: 'Enhanced profanity filtering rules.' },
      { version: '1.3', date: '2025-01-20', changes: 'Added malicious content detection rules.' },
      { version: '1.2', date: '2025-01-05', changes: 'Updated personal identifier redaction rules.' },
      { version: '1.1', date: '2024-12-20', changes: 'Minor updates to content scanning requirements.' },
      { version: '1.0', date: '2024-12-10', changes: 'Initial policy creation.' }
    ],
    complianceStatus: [
      { agentId: 'AGT-134', agentName: 'Content Creator', pipelineId: 'PIP-002', status: 'Compliant', lastChecked: '2025-03-17' },
      { agentId: 'AGT-145', agentName: 'Customer Support', pipelineId: 'PIP-003', status: 'Compliant', lastChecked: '2025-03-16' },
      { agentId: 'AGT-178', agentName: 'Social Media Bot', pipelineId: 'PIP-009', status: 'Warning', lastChecked: '2025-03-15', issues: ['Incomplete profanity filtering'] }
    ]
  },
  {
    id: 'POL-SEC-003',
    name: 'Tool Usage Restrictions',
    description: 'Defines limitations and security controls for agent usage of external tools and APIs.',
    version: '1.2',
    status: 'Active',
    category: 'Tool Security',
    tags: ['API Access', 'Tool Control', 'External Systems'],
    rules: [
      'Agents must only use approved and vetted external tools and APIs.',
      'All API calls must use secure authentication methods and encrypted connections.',
      'Tool usage must be logged with detailed information about inputs and outputs.',
      'Agents must not have access to tools that can modify production systems without human approval.',
      'Rate limiting must be implemented for all external API calls.'
    ],
    appliesTo: ['Automation Agents', 'Development Assistants', 'Integration Pipelines'],
    createdBy: 'David Chen',
    createdAt: '2025-01-05',
    updatedAt: '2025-02-10',
    versionHistory: [
      { version: '1.2', date: '2025-02-10', changes: 'Added rate limiting requirements.' },
      { version: '1.1', date: '2025-01-20', changes: 'Enhanced logging requirements for tool usage.' },
      { version: '1.0', date: '2025-01-05', changes: 'Initial policy creation.' }
    ],
    complianceStatus: [
      { agentId: 'AGT-156', agentName: 'Data Analyzer', pipelineId: 'PIP-007', status: 'Compliant', lastChecked: '2025-03-17' },
      { agentId: 'AGT-189', agentName: 'Code Assistant', pipelineId: 'PIP-012', status: 'Violation', lastChecked: '2025-03-16', issues: ['Unauthorized API access', 'Missing rate limiting'] },
      { agentId: 'AGT-167', agentName: 'System Integrator', pipelineId: 'PIP-008', status: 'Warning', lastChecked: '2025-03-15', issues: ['Incomplete logging'] }
    ]
  },
  {
    id: 'POL-SEC-004',
    name: 'Prompt Injection Prevention',
    description: 'Protects against prompt injection attacks and other input manipulation techniques.',
    version: '1.0',
    status: 'Active',
    category: 'Input Security',
    tags: ['Prompt Security', 'Input Validation', 'Attack Prevention'],
    rules: [
      'All user inputs must be validated and sanitized before processing by agents.',
      'System prompts and instructions must be protected from user modification.',
      'Agents must be tested against common prompt injection techniques.',
      'Suspicious inputs should trigger additional verification steps.',
      'Agents should be regularly audited for vulnerabilities to prompt manipulation.'
    ],
    appliesTo: ['All Agents', 'User-Facing Pipelines'],
    createdBy: 'Sarah Williams',
    createdAt: '2025-02-20',
    updatedAt: '2025-02-20',
    versionHistory: [
      { version: '1.0', date: '2025-02-20', changes: 'Initial policy creation.' }
    ],
    complianceStatus: [
      { agentId: 'AGT-134', agentName: 'Content Creator', pipelineId: 'PIP-002', status: 'Warning', lastChecked: '2025-03-17', issues: ['Incomplete input validation'] },
      { agentId: 'AGT-145', agentName: 'Customer Support', pipelineId: 'PIP-003', status: 'Compliant', lastChecked: '2025-03-16' },
      { agentId: 'AGT-123', agentName: 'Claims Processor', pipelineId: 'PIP-001', status: 'Compliant', lastChecked: '2025-03-15' }
    ]
  },
  {
    id: 'POL-SEC-005',
    name: 'Model Guardrails',
    description: 'Defines guardrails and safety measures for AI model behavior.',
    version: '2.1',
    status: 'Active',
    category: 'Model Safety',
    tags: ['Guardrails', 'Model Behavior', 'Safety'],
    rules: [
      'Models must refuse to generate harmful, illegal, or unethical content.',
      'Models must have built-in topic boundaries for sensitive areas.',
      'All models must undergo safety testing before deployment.',
      'Runtime monitoring must be implemented to detect unsafe model behavior.',
      'Models must provide appropriate disclaimers when discussing sensitive topics.'
    ],
    appliesTo: ['All LLM-based Agents', 'Content Generation Pipelines'],
    createdBy: 'Michael Brown',
    createdAt: '2024-11-10',
    updatedAt: '2025-01-15',
    versionHistory: [
      { version: '2.1', date: '2025-01-15', changes: 'Added runtime monitoring requirements.' },
      { version: '2.0', date: '2024-12-20', changes: 'Major revision with enhanced safety testing requirements.' },
      { version: '1.0', date: '2024-11-10', changes: 'Initial policy creation.' }
    ],
    complianceStatus: [
      { agentId: 'AGT-134', agentName: 'Content Creator', pipelineId: 'PIP-002', status: 'Compliant', lastChecked: '2025-03-17' },
      { agentId: 'AGT-178', agentName: 'Social Media Bot', pipelineId: 'PIP-009', status: 'Violation', lastChecked: '2025-03-16', issues: ['Missing topic boundaries', 'Inadequate safety testing'] },
      { agentId: 'AGT-145', agentName: 'Customer Support', pipelineId: 'PIP-003', status: 'Compliant', lastChecked: '2025-03-15' }
    ]
  },
  {
    id: 'POL-SEC-006',
    name: 'Agent Authentication',
    description: 'Defines authentication and identity verification requirements for agents.',
    version: '1.3',
    status: 'Draft',
    category: 'Access Control',
    tags: ['Authentication', 'Identity', 'Security'],
    rules: [
      'All agents must have unique identifiers and credentials.',
      'Agent authentication must use multi-factor authentication where possible.',
      'Credentials must be rotated regularly and stored securely.',
      'Failed authentication attempts must be logged and monitored.',
      'Agent identity must be verified before granting access to sensitive systems.'
    ],
    appliesTo: ['All Agents', 'System Integration Pipelines'],
    createdBy: 'Jennifer Lee',
    createdAt: '2025-03-01',
    updatedAt: '2025-03-10',
    versionHistory: [
      { version: '1.3', date: '2025-03-10', changes: 'Added credential rotation requirements.' },
      { version: '1.2', date: '2025-03-05', changes: 'Enhanced multi-factor authentication requirements.' },
      { version: '1.1', date: '2025-03-03', changes: 'Added logging requirements for failed authentication.' },
      { version: '1.0', date: '2025-03-01', changes: 'Initial policy creation.' }
    ],
    complianceStatus: []
  },
  {
    id: 'POL-SEC-007',
    name: 'Conversation Memory Limits',
    description: 'Defines limits and controls for agent conversation memory and context retention.',
    version: '1.0',
    status: 'Deprecated',
    category: 'Data Privacy',
    tags: ['Memory', 'Context', 'Privacy'],
    rules: [
      'Agent conversation memory must be limited to the minimum necessary duration.',
      'Sensitive information must be automatically purged from memory after use.',
      'Users must be able to request deletion of conversation history.',
      'Long-term memory storage must be explicitly opt-in by users.',
      'Memory retention policies must be clearly communicated to users.'
    ],
    appliesTo: ['Conversational Agents', 'Customer Support Pipelines'],
    createdBy: 'Robert Taylor',
    createdAt: '2024-10-15',
    updatedAt: '2025-02-28',
    versionHistory: [
      { version: '1.0', date: '2024-10-15', changes: 'Initial policy creation.' }
    ],
    complianceStatus: [
      { agentId: 'AGT-145', agentName: 'Customer Support', pipelineId: 'PIP-003', status: 'Compliant', lastChecked: '2025-02-25' },
      { agentId: 'AGT-178', agentName: 'Social Media Bot', pipelineId: 'PIP-009', status: 'Compliant', lastChecked: '2025-02-25' }
    ]
  }
];

export default Policies;
